import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const round = (n) => Math.round(n * 1e5) / 1e5;

// Return a copy of `boundary` with the vertex at `idx` replaced by `latlng`.
// Extracted to module scope so the map callback isn't nested inside the
// forEach → dragend handler chain (sonar S2004: max 4 levels of nesting).
function moveVertex(boundary, idx, latlng) {
  return boundary.map((vtx, i) => (i === idx ? [round(latlng.lat), round(latlng.lng)] : vtx));
}

const vertexIcon = L.divIcon({
  className: 'poly-vertex-icon',
  html: '<span class="poly-vertex-dot"></span>',
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

// Build a draggable vertex marker. The dragend handler lives here at module
// scope so it is not nested inside the useEffect → forEach → on('dragend')
// chain (sonar S2004: max 4 levels of function nesting).
function createVertexMarker({ point, idx, map, editable, boundaryRef, onChangeRef }) {
  const marker = L.marker(point, { draggable: editable, icon: vertexIcon }).addTo(map);
  if (editable) {
    marker.on('dragend', () => {
      const next = moveVertex(boundaryRef.current, idx, marker.getLatLng());
      onChangeRef.current?.(next);
    });
  }
  return marker;
}

/**
 * Lightweight Leaflet polygon editor for a port geofence boundary.
 *
 * - Renders the current `boundary` ([[lat, lng], ...]) as an editable polygon.
 * - Click the map to append a vertex; drag a vertex marker to move it.
 * - Every edit calls `onChange(nextBoundary)` so the parent stays authoritative.
 *
 * The map is a visual aid; the parent also exposes a coordinates field, so the
 * feature works even if map tiles fail to load.
 */
export default function PolygonMap({ boundary = [], onChange, editable = true, height = 320 }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const polyRef = useRef(null);
  const markersRef = useRef([]);
  const boundaryRef = useRef(boundary);
  const onChangeRef = useRef(onChange);
  boundaryRef.current = boundary;
  onChangeRef.current = onChange;

  // Initialize the map once.
  useEffect(() => {
    const map = L.map(containerRef.current, {
      center: [14.59, 120.96],
      zoom: 11,
      scrollWheelZoom: false,
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);

    if (editable) {
      map.on('click', (e) => {
        const next = [...boundaryRef.current, [round(e.latlng.lat), round(e.latlng.lng)]];
        onChangeRef.current?.(next);
      });
    }
    mapRef.current = map;
    // Leaflet needs a re-measure once the container has its final size.
    setTimeout(() => map.invalidateSize(), 0);

    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Redraw polygon + vertex markers whenever the boundary changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (polyRef.current) {
      polyRef.current.remove();
      polyRef.current = null;
    }
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    const pts = Array.isArray(boundary) ? boundary : [];
    if (pts.length >= 2) {
      polyRef.current = L.polygon(pts, { color: '#667eea', weight: 2, fillOpacity: 0.15 }).addTo(map);
    }

    pts.forEach((p, idx) => {
      const marker = createVertexMarker({
        point: p,
        idx,
        map,
        editable,
        boundaryRef,
        onChangeRef,
      });
      markersRef.current.push(marker);
    });

    if (pts.length >= 1 && polyRef.current) {
      try {
        map.fitBounds(polyRef.current.getBounds(), { padding: [24, 24], maxZoom: 14 });
      } catch {
        /* empty bounds — ignore */
      }
    }
  }, [boundary, editable]);

  return <div ref={containerRef} data-testid="port-map" style={{ height, width: '100%', borderRadius: 8 }} />;
}
